/**
 * AuditLogPage - Página de log de auditoría
 * Solo accesible por administradores y auditores
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Grid,
  TextField,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Card,
  CardContent,
  Alert,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  FileDownload as ExportIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import {
  PageHeader,
  LoadingSpinner,
  DateRangePicker,
} from '../components/common';
import { useNotification } from '../context/NotificationContext';
import { useAuth } from '../context/AuthContext';
import * as auditoriaService from '../services/auditoriaService';
import * as catalogoService from '../services/catalogoService';
import {
  formatDateTime,
  AUDIT_ACTION_LABELS,
  AUDIT_TABLE_LABELS,
  ROLES,
  downloadBlob,
} from '../utils';

const AuditLogPage = () => {
  const { showSuccess, showError } = useNotification();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [auditLogs, setAuditLogs] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalRecords, setTotalRecords] = useState(0);

  // Filtros
  const [filters, setFilters] = useState({
    usuario_id: '',
    tipo_accion: '',
    tabla_afectada: '',
    fecha_desde: null,
    fecha_hasta: null,
  });

  // Verificar permisos
  useEffect(() => {
    if (
      user &&
      user.rol !== ROLES.ADMINISTRADOR &&
      user.rol !== ROLES.AUDITOR
    ) {
      showError('No tiene permisos para acceder a esta página');
      window.history.back();
    }
  }, [user]);

  // Cargar datos iniciales
  const loadData = async () => {
    try {
      setLoading(true);

      // Cargar usuarios para el filtro - Solo si es administrador
      // Los auditores no tienen permiso para ver usuarios
      if (user?.rol === 'administrador') {
        try {
          const usuariosData = await catalogoService.getUsuarios();
          setUsuarios(usuariosData);
        } catch (error) {
          console.error('Error al cargar usuarios:', error);
          // No mostrar error, simplemente continuar sin usuarios
        }
      }

      // Cargar logs
      await loadAuditLogs();
    } catch (error) {
      console.error('Error al cargar datos:', error);
      showError('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user?.rol]);

  // Cargar logs de auditoría
  const loadAuditLogs = async () => {
    try {
      setLoading(true);

      const params = {
        ...filters,
        page: page + 1,
        limit: rowsPerPage,
      };

      // Limpiar filtros vacíos
      Object.keys(params).forEach((key) => {
        if (params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });

      const response = await auditoriaService.getAuditLog(params);

      setAuditLogs(response.logs || response.data || response);
      setTotalRecords(response.total || response.length || 0);
    } catch (error) {
      console.error('Error al cargar logs de auditoría:', error);
      showError('Error al cargar logs de auditoría');
    } finally {
      setLoading(false);
    }
  };

  // Aplicar filtros
  useEffect(() => {
    if (!loading) {
      setPage(0);
      loadAuditLogs();
    }
  }, [filters]);

  // Cambiar página
  useEffect(() => {
    if (!loading) {
      loadAuditLogs();
    }
  }, [page, rowsPerPage]);

  // Manejar cambio de filtro
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Manejar cambio de rango de fechas
  const handleDateRangeChange = (startDate, endDate) => {
    setFilters((prev) => ({
      ...prev,
      fecha_desde: startDate,
      fecha_hasta: endDate,
    }));
  };

  // Expandir/contraer fila
  const handleExpandRow = (logId) => {
    setExpandedRow(expandedRow === logId ? null : logId);
  };

  // Ver historial del cheque
  const handleViewChequeHistory = (chequeId) => {
    navigate(`/cheques/${chequeId}`);
  };

  // Exportar a Excel
  const handleExportExcel = async () => {
    try {
      setLoading(true);

      const params = { ...filters };
      Object.keys(params).forEach((key) => {
        if (params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });

      const blob = await auditoriaService.exportAuditToExcel(params);
      downloadBlob(blob, `auditoria_${new Date().getTime()}.xlsx`);

      showSuccess('Reporte exportado correctamente');
    } catch (error) {
      console.error('Error al exportar reporte:', error);
      showError('Error al exportar reporte');
    } finally {
      setLoading(false);
    }
  };

  // Renderizar diferencias
  const renderDifferences = (log) => {
    if (!log.valores_anteriores && !log.valores_nuevos) {
      return <Typography>Sin cambios registrados</Typography>;
    }

    const oldValues = log.valores_anteriores || {};
    const newValues = log.valores_nuevos || {};

    const allKeys = new Set([
      ...Object.keys(oldValues),
      ...Object.keys(newValues),
    ]);

    return (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Campo</TableCell>
              <TableCell>Valor Anterior</TableCell>
              <TableCell>Valor Nuevo</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from(allKeys).map((key) => {
              const oldValue = JSON.stringify(oldValues[key] || '-');
              const newValue = JSON.stringify(newValues[key] || '-');
              const hasChanged = oldValue !== newValue;

              return (
                <TableRow
                  key={key}
                  sx={{
                    backgroundColor: hasChanged
                      ? 'warning.lighter'
                      : 'transparent',
                  }}
                >
                  <TableCell>
                    <strong>{key}</strong>
                  </TableCell>
                  <TableCell>{oldValue}</TableCell>
                  <TableCell sx={{ color: hasChanged ? 'error.main' : 'inherit' }}>
                    {newValue}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Obtener color de acción
  const getActionColor = (action) => {
    const colors = {
      CREATE: 'success',
      UPDATE: 'info',
      DELETE: 'error',
      LOGIN: 'primary',
      LOGOUT: 'default',
      CAMBIO_ESTADO: 'warning',
      CAMBIO_FECHA: 'warning',
    };

    return colors[action] || 'default';
  };

  if (loading && auditLogs.length === 0) {
    return <LoadingSpinner message="Cargando logs de auditoría..." />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Log de Auditoría"
        breadcrumbs={[{ label: 'Auditoría' }]}
        actions={[
          {
            label: 'Exportar Excel',
            icon: ExportIcon,
            onClick: handleExportExcel,
            variant: 'outlined',
          },
        ]}
      />

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <DateRangePicker
              startDate={filters.fecha_desde}
              endDate={filters.fecha_hasta}
              onChange={handleDateRangeChange}
              label="Rango de fechas"
            />
          </Grid>

          {/* Filtro de usuario - Solo para administradores */}
          {user?.rol === 'administrador' && (
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Usuario"
                value={filters.usuario_id}
                onChange={(e) => handleFilterChange('usuario_id', e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                {usuarios.map((usuario) => (
                  <MenuItem key={usuario.id} value={usuario.id}>
                    {usuario.nombre}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}

          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Tipo de Acción"
              value={filters.tipo_accion}
              onChange={(e) => handleFilterChange('tipo_accion', e.target.value)}
            >
              <MenuItem value="">Todas</MenuItem>
              {Object.entries(AUDIT_ACTION_LABELS).map(([key, label]) => (
                <MenuItem key={key} value={key}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Tabla Afectada"
              value={filters.tabla_afectada}
              onChange={(e) =>
                handleFilterChange('tabla_afectada', e.target.value)
              }
            >
              <MenuItem value="">Todas</MenuItem>
              {Object.entries(AUDIT_TABLE_LABELS).map(([key, label]) => (
                <MenuItem key={key} value={key}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabla de logs */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Fecha/Hora</TableCell>
                <TableCell>Usuario</TableCell>
                <TableCell>Acción</TableCell>
                <TableCell>Tabla</TableCell>
                <TableCell>Registro ID</TableCell>
                <TableCell>IP</TableCell>
                <TableCell>Detalles</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {auditLogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Alert severity="info">No hay registros de auditoría</Alert>
                  </TableCell>
                </TableRow>
              ) : (
                auditLogs.map((log) => (
                  <>
                    <TableRow key={log._id} hover>
                      <TableCell>{formatDateTime(log.fecha)}</TableCell>
                      <TableCell>
                        {log.usuario_id?.nombre || log.usuario_nombre || '-'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={AUDIT_ACTION_LABELS[log.tipo_accion] || log.tipo_accion}
                          color={getActionColor(log.tipo_accion)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {AUDIT_TABLE_LABELS[log.tabla_afectada] || log.tabla_afectada}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                        {log.registro_id?.substring(0, 8)}...
                      </TableCell>
                      <TableCell>{log.ip_address || '-'}</TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          onClick={() => handleExpandRow(log._id)}
                        >
                          {expandedRow === log._id ? 'Ocultar' : 'Ver'}
                        </Button>
                      </TableCell>
                    </TableRow>

                    {expandedRow === log._id && (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <Accordion defaultExpanded>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography variant="subtitle2">
                                Detalles del Cambio
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Grid container spacing={2}>
                                <Grid item xs={12}>
                                  <Card variant="outlined">
                                    <CardContent>
                                      <Typography
                                        variant="subtitle2"
                                        gutterBottom
                                      >
                                        Diferencias
                                      </Typography>
                                      {renderDifferences(log)}
                                    </CardContent>
                                  </Card>
                                </Grid>

                                {log.cheque_id && (
                                  <Grid item xs={12}>
                                    <Button
                                      variant="outlined"
                                      startIcon={<ViewIcon />}
                                      onClick={() =>
                                        handleViewChequeHistory(log.cheque_id)
                                      }
                                    >
                                      Ver Historial del Cheque
                                    </Button>
                                  </Grid>
                                )}
                              </Grid>
                            </AccordionDetails>
                          </Accordion>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalRecords}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Registros por página:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count}`
          }
        />
      </Paper>
    </Box>
  );
};

export default AuditLogPage;
